import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, FormArray, Validators, FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { BaseComponent } from '../base-component';
import { AFCriminalHistoryDomain, AFFitnessAndProprietyDomain } from '../model/af.model';
import { AuthService } from '../service/auth.service';
import { CriminalHistoryService } from '../service/criminalhistory.service';
import { EventService } from '../service/event.service';
import { ProgressBarEvent, ChangeNavigationEvent, ValidateAndNavigateEvent, BeforeSubmitErrorEvent, SkipToMainContentEvent } from '../service/events';
import { Title } from '@angular/platform-browser';

// Define the form value interfaces using FormControl
interface AFCountryAnswersFormGroup {
  id: FormControl<number | null>;
  afId: FormControl<number | null>;
  questionId: FormControl<number | null>;
  questions: FormControl<string>;
  answer: FormControl<number | null>;
  description: FormControl<string>;
  category: FormControl<string>;
  countryCd: FormControl<string>;
}

/**
 * Component for handling fitness and propriety questions in the application for OUS, including credit and criminal types.
 */
@Component({
  selector: 'app-fitness-propriety',
  templateUrl: './fitness-propriety.component.html',
  styleUrl: './fitness-propriety.component.scss',
  standalone: false
})
export class FitnessProprietyComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

  fitnessAndProprietyData: AFFitnessAndProprietyDomain;
  fitnessAndProprietyForm: FormGroup<{
    submit: FormControl<boolean>;
    verifyId: FormControl<string>;
    bookmarkUrl: FormControl<string>;
    afCountryQuestionsList: FormArray<FormGroup<AFCountryAnswersFormGroup>>;
  }>;
  hasBeenSubmittted: boolean = false;
  questions_Category: Array<string> = [];
  isIndiaGBS: boolean = false;

  constructor(private userService: AuthService, private eventBus: EventService, private criminalHistoryService: CriminalHistoryService, private fb: FormBuilder,
    private _router: Router, private _snackBar: MatSnackBar, private titleService: Title) {
    super(eventBus, _router)
  }

  get submit() { return this.fitnessAndProprietyForm.get('submit') }
  get verifyId() { return this.fitnessAndProprietyForm.get('verifyId') }
  get bookmarkUrl() { return this.fitnessAndProprietyForm.get('bookmarkUrl') }
  get afCountryQuestionsList() { return this.fitnessAndProprietyForm.get('afCountryQuestionsList') as FormArray<FormGroup<AFCountryAnswersFormGroup>>; }

  ngOnInit() {
    if (this.userService.getJobCountry() === 'IND' && this.userService.getJobSubCountry() === 'GBS') {
      this.isIndiaGBS = true;
      this.titleService.setTitle("Applicant Form | Criminal History");
    }

    this.createGroup();
    this.subscribeEvents();
  }

  ngAfterViewInit() {
    this.fitnessAndProprietyForm.valueChanges.subscribe(() => {

      setTimeout(() => {
        let progress = this.getPercentage(this.fitnessAndProprietyForm);
        this.eventBus.emit(new ProgressBarEvent({ id: progress }));
      }, 100);
    });

    // retain navigation on page refresh
    let navlink = this.userService.getNavLinks().find(e => e.routerLink === "/fitnessPropriety");
    this.eventBus.emit(new ChangeNavigationEvent({ id: navlink.id }));
  }

  getPercentage(fitnessAndProprietyForm: FormGroup<{
    submit: FormControl<boolean>;
    verifyId: FormControl<string>;
    bookmarkUrl: FormControl<string>;
    afCountryQuestionsList: FormArray<FormGroup<AFCountryAnswersFormGroup>>;
  }>): number {
    let errorCount = 0;
    let totalCount = 0;

    for (let container of fitnessAndProprietyForm.get('afCountryQuestionsList')['controls']) {
      for (let controlKey in container.controls) {
        if (container.controls.hasOwnProperty(controlKey)) {
          if (container.controls[controlKey].validator != null) {
            totalCount += container.controls[controlKey].validator.length;
          }
          if (container.controls[controlKey].errors) {
            errorCount += Object.keys(container.controls[controlKey].errors).length;
          }
        }
      }
    }

    let percentage = ((totalCount - errorCount) / totalCount) * 100;

    return percentage;
  }

  subscribeEvents() {
    if (!this.isIndiaGBS) {
      this.criminalHistoryService.getFitnessAndProprietyQuestions().subscribe({
        next: (data: AFFitnessAndProprietyDomain) => {
          this.fitnessAndProprietyData = data;
          this.fitnessAndProprietyData.verifyId ??= this.userService.getVerifyId();
          this.fitnessAndProprietyForm.patchValue(this.fitnessAndProprietyData);
          this.populateCountryQuestions();
        },
        error: (error: Error) => {
          console.log(error);
        }
      });
    } else {
      this.criminalHistoryService.getCriminalHistory(true).subscribe({
        next: (data: AFCriminalHistoryDomain) => {
          this.fitnessAndProprietyData = data;
          this.fitnessAndProprietyData.verifyId ??= this.userService.getVerifyId();
          this.fitnessAndProprietyForm.patchValue(this.fitnessAndProprietyData);
          this.populateCountryQuestions();
        },
        error: (error: Error) => {
          console.log(error);
        }
      });
    }

    this.sub = this.eventBus.on(
      ValidateAndNavigateEvent,
      (event): void => {
        if (event.payload.currentLink === '/fitnessPropriety') {
          this.submit.setValue(false);
          this.bookmarkUrl.setValue("");
          if (event.payload.save) {
            this.submitFitnessAndPropriety((this.fitnessAndProprietyForm.value), event);
          } else if (event.payload.saveAndFinishLater) {
            this.bookmarkUrl.setValue(event.payload.currentLink);
            this.submitFitnessAndPropriety((this.fitnessAndProprietyForm.value), event);
          } else if (this.validate()) {
            this.submitFitnessAndPropriety((this.fitnessAndProprietyForm.value), event);
          } else {
            this.eventBus.emit(new BeforeSubmitErrorEvent({}));
          }
        }
      }
    );

    this.eventBus.on(
      SkipToMainContentEvent,
      (event): void => {
        if (event.payload.text && event.payload.text == "/fitnessPropriety") {
          let mainContentElement = document.querySelector("#main-content"); //check if main-content id exists on page
          if (mainContentElement) {
            mainContentElement.scrollIntoView(true);
          }
        }
      }
    );
  }

  onFocusOutMainContent(event) {
    this.eventBus.emit(new SkipToMainContentEvent({ text: "FocusOff" }));
  }

  submitFitnessAndPropriety(fitnessAndProprietyForm, event) {
    this.criminalHistoryService.submitFitnessAndProprietyQuestions(fitnessAndProprietyForm).subscribe({
      next: () => {
        if (event.payload.save) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
        } else if (event.payload.saveAndFinishLater) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
          this._router.navigate(['sessionTimedOut']);
        } else {
          this.eventBus.emit(new ChangeNavigationEvent({ id: event.payload.id }));
          this._router.navigateByUrl(event.payload.nextNavLink);
        }
      },
      error: (error: any) => console.log(error)
    });
  }

  createGroup() {
    this.fitnessAndProprietyForm = this.fb.group({
      submit: this.fb.control(false),
      verifyId: this.fb.control(this.userService.getVerifyId()),
      bookmarkUrl: this.fb.control(''),
      afCountryQuestionsList: this.fb.array([] as FormGroup<AFCountryAnswersFormGroup>[], [])
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  validate() {
    this.submit.setValue(true);
    this.hasBeenSubmittted = true;

    this.fitnessAndProprietyForm.markAllAsTouched();
    return !this.fitnessAndProprietyForm.invalid;
  }

  countryQuestionsGroup(): FormGroup<AFCountryAnswersFormGroup> {
    return this.fb.group({
      id: this.fb.control<number | null>(null),
      afId: this.fb.control<number | null>(null),
      questionId: this.fb.control<number | null>(null),
      questions: this.fb.control<string>(''),
      answer: this.fb.control<number | null>(null, Validators.required),
      description: this.fb.control<string>(''),
      category: this.fb.control<string>(''),
      countryCd: this.fb.control<string>(''),
    });
  }

  populateCountryQuestions() {
    this.questions_Category = this.fitnessAndProprietyData.afCountryQuestionsList.map(item => item.category).filter((value, index, self) => self.indexOf(value) === index)
    for (let questions of this.fitnessAndProprietyData.afCountryQuestionsList) {
      let fg = this.countryQuestionsGroup();
      fg.patchValue({
        id: questions.id ?? null,
        afId: questions.afId ?? null,
        questionId: questions.questionId ?? null,
        questions: questions.questions ?? '',
        answer: questions.answer ?? null,
        description: questions.description ?? '',
        category: questions.category ?? '',
        countryCd: questions.countryCd ?? '',
      });
      this.afCountryQuestionsList.push(fg);
    }
  }

  getQuestionsbasedCategory(category: string) {
    return this.afCountryQuestionsList.controls.filter(a => a.get('category')?.value?.toUpperCase() == category.toUpperCase());
  }
}
